'use client';

import type { CommentableType, IComment } from '@/lib/types/comment';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useComments } from '@/lib/hooks/useComments';
import { CommentItem } from './CommentItem';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Skeleton } from '@/components/ui/skeleton';
import { useBearStore } from '@/lib/stores/user';
import { FaRegCommentDots } from "react-icons/fa";

const commentSchema = z.object({
  content: z.string().min(1, 'Bình luận không được để trống'),
});

interface CommentsProps {
  commentableType: CommentableType;
  commentableId: number;
  type?: 'blog' | 'public' | 'private' | 'lesson';
}

export const Comments = ({
  commentableType,
  commentableId,
  type = 'public',
}: CommentsProps) => {
  const [showCommentForm, setShowCommentForm] = useState(false);
  const {
    comments,
    isLoading,
    fetchComments,
    addComment,
    updateComment,
    deleteComment,
    hasNextPage,
    loadMore,
  } = useComments({
    commentableType,
    commentableId,
    token: typeof window !== 'undefined' ? localStorage.getItem('token') || undefined : undefined
  });

  const { profileUser } = useBearStore()
  const currentUserId = profileUser.id

  const commentTextareaRef = useRef<HTMLTextAreaElement | null>(null)

  // Sắp xếp bình luận theo cấu trúc phân cấp
  const organizedComments = useMemo(() => {
    const commentMap = new Map<number, IComment>();
    const rootComments: IComment[] = [];

    comments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, replies: [] });
    });

    comments.forEach(comment => {
      const commentWithReplies = commentMap.get(comment.id)!;

      if (comment.parent_id) {
        const parentComment = commentMap.get(comment.parent_id);
        if (parentComment) {
          parentComment.replies = parentComment.replies || [];
          parentComment.replies.push(commentWithReplies);
        } else {
          rootComments.push(commentWithReplies);
        }
      } else {
        rootComments.push(commentWithReplies);
      }
    });

    return rootComments.sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [comments]);

  const form = useForm<z.infer<typeof commentSchema>>({
    resolver: zodResolver(commentSchema),
    defaultValues: {
      content: '',
    },
  });

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const onSubmit = async (values: z.infer<typeof commentSchema>) => {
    await addComment({
      content: values.content,
      type,
    });
    form.reset();
    setShowCommentForm(false);
  };

  const handleReply = async (parentId: number, content: string) => {
    await addComment({
      content,
      type,
      parent_id: parentId,
    });
  };

  const handleUpdate = async (id: number, content: string) => {
    await updateComment(id, { content });
  };

  const handleDelete = async (id: number) => {
    await deleteComment(id);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-start gap-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const renderCommentForm = () => {
    if (!currentUserId) return null;

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    placeholder="Viết bình luận của bạn..."
                    className="min-h-[100px]"
                    {...field}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        if (field.value?.trim()) {
                          form.handleSubmit(onSubmit)();
                        }
                      }
                    }}
                    onRef={(e) => {
                      field.ref(e);
                      if (commentTextareaRef) {
                        commentTextareaRef.current = e;
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCommentForm(false)}
            >
              Hủy
            </Button>
            <Button type="submit">Đăng bình luận</Button>
          </div>
        </form>
      </Form>
    );
  };

  return (
    <div className="space-y-8">
      {!organizedComments.length && !showCommentForm && currentUserId > 0 && (
        <div className="flex flex-col items-center gap-4">
          <div className="flex flex-col items-center gap-2 py-8">
            <FaRegCommentDots className="h-12 w-12 text-muted-foreground/50" />
            <p className="text-muted-foreground text-sm">Chưa có bình luận</p>
          </div>
          <Button
            onClick={() => {
              setShowCommentForm(true);
              setTimeout(() => {
                commentTextareaRef.current?.focus();
              }, 0);
            }}
            className="w-full max-w-[200px]"
          >
            Viết bình luận đầu tiên
          </Button>
        </div>
      )}

      {!organizedComments.length && !showCommentForm && !currentUserId && (
        <div className="flex flex-col items-center gap-2 py-8">
          <FaRegCommentDots className="h-12 w-12 text-muted-foreground/50" />
          <p className="text-muted-foreground text-sm">Chưa có bình luận</p>
        </div>
      )}

      {organizedComments.length > 0 && !showCommentForm && currentUserId > 0 && (
        <Button
          onClick={() => {
            setShowCommentForm(true);
            setTimeout(() => {
              commentTextareaRef.current?.focus();
            }, 0);
          }}
          variant="outline"
          className="w-full"
        >
          Viết bình luận
        </Button>
      )}

      {showCommentForm && renderCommentForm()}

      {organizedComments.length > 0 && (
        <div className="space-y-6">
          {organizedComments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onReply={handleReply}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              currentUserId={currentUserId}
            />
          ))}

          {hasNextPage && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                onClick={loadMore}
                disabled={isLoading}
                className="w-full max-w-[200px]"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Đang tải...
                  </div>
                ) : (
                  'Xem thêm'
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 